<template>
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else-if="canViewWiFiProfile || user?.is_staff" :class="embedded ? 'dark:text-white' : 'p-6 dark:text-white'">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 v-if="!embedded" class="text-3xl font-bold text-gray-900 dark:text-white">
        WiFi Profiles - {{ filteredConfigs.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div v-else class="text-sm text-gray-500 dark:text-gray-400">
        {{ filteredConfigs.length }} profile{{ filteredConfigs.length === 1 ? '' : 's' }}
        <span v-if="isFiltered">(filtered)</span>
      </div>

      <div class="mb-4 text-base flex space-x-3 justify-between">
        <button v-if="canAddWiFiProfile || user?.is_staff"
          @click="$router.push({ name: 'AddWiFiProfile' })"
          class="bg-blue-700 text-white px-6 py-2 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
        >
          Add WiFi Profile
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search profiles by name..."
        />
      </div>

      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="bulkActionOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Bulk Actions"
          class="!w-44"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedConfigs.length === 0"
          :class="[
            'px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
            bulkAction && selectedConfigs.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- WiFi Profiles Table -->
    <GenericTable
      ref="table"
      :columns="columns"
      :items="paginatedConfigs"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedConfigs"
      @rowClick="goToConfigDetail"
    >
      <template #pending="{ value }">
        <span :class="[
          'px-2 py-1 text-xs rounded-full',
          value ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
        ]">
          {{ value ? 'Pending' : 'Approved' }}
        </span>
      </template>

      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0">
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="pageSizeOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Page size"
          class="!w-36"
          @change="updatePagination"
        />
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingItem">
      <div
        @click.self="deletingItem=false"
        class="items-center justify-center inset-0 absolute bg-gray-600 bg-opacity-50 flex z-10">
        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 dark:bg-gray-900 w-[65%] h-[35%] m-auto shadow-xl items-center text-center z-50">
          <div class="mb-2 text-base flex space-x-2 justify-between">
            <span class="font-semibold">Are you sure you want to delete the following?</span>
            <button @click="deletingItem=false" class="hover:bg-white dark:hover:bg-gray-900">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="overflow-auto w-[90%] h-[70%] m-auto items-center text-center mb-4 bg-gray-100 dark:bg-gray-800 rounded">
            <div class="my-6">
              <p class="font-semibold justify-left flex pl-4">WiFi Profiles: {{ selectedConfigs.length }}</p>
              <p v-for="item in selectedConfigs" :key="item.id" class="font-medium justify-left flex pl-4">{{ item.name }}</p>
            </div>
          </div>
          <div class="text-base flex space-x-2 justify-center mt-6">
            <button
              class="rounded-md text-gray-500 dark:text-white dark:hover:bg-gray-900 hover:text-red-500 hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
              @click="confirmDelete">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                <line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>
              </svg>
            </button>
            <button
              class="bg-blue-600 text-white px-6 py-1 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              @click="deletingItem=false">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPersistedPageSize, setPersistedPageSize } from "@/composables/usePersistedPageSize";
import { getAllWiFiProfiles, deleteWiFiProfile } from "@/services/wifiProfileService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { formatError } from "@/utils/errorMessages";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

import GlassSelect from "@/components/GlassSelect.vue";
export default {
  name: "WiFiProfileListView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
   GlassSelect,},
  props: {
    embedded: { type: Boolean, default: false },
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      configs: [],
      searchQuery: "",
      filteredConfigs: [],
      currentPage: 1,
      itemsPerPage: getPersistedPageSize(),
      selectedConfigs: [],
      deletingItem: false,
      bulkAction: "",
      columns: [
        { key: "name", label: "Name", sortable: true, width: "20%" },
        { key: "ssid", label: "SSID", sortable: true, width: "15%" },
        { key: "auth_type", label: "Auth Type", sortable: true, width: "12%" },
        { key: "org_name", label: "Organisation", sortable: true, width: "15%" },
        { key: "linked_probe_count", label: "Assigned Probes", sortable: true, width: "10%" },
        { key: "pending", label: "Status", sortable: true, width: "8%" },
        { key: "created_date", label: "Created", sortable: true, width: "20%" },
      ],
      canAddWiFiProfile: false,
      canDeleteWiFiProfile: false,
      canViewWiFiProfile: false,
      error: "",
      errorCode: "",
    };
  },
  computed: {
    pageSizeOptions() {
      return [
        { value: 10, label: '10 per page' },
        { value: 20, label: '20 per page' },
        { value: 50, label: '50 per page' },
        { value: 100, label: '100 per page' },
        { value: -1, label: 'All' },
      ];
    },
    bulkActionOptions() {
      const opts = [];
      if (this.canDeleteWiFiProfile || this.user?.is_staff) {
        opts.push({ value: 'delete', label: 'Delete selected items' });
      }
      return opts;
    },

    user() {
      return useStore.getUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredConfigs.length / this.itemsPerPage);
    },
    paginatedConfigs() {
      if (this.itemsPerPage === -1) {
        return this.filteredConfigs;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredConfigs.slice(start, end);
    },
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();
    if (this.embedded) {
      await this.fetchConfigsGlobal();
    } else {
      await this.tenant.loadInitialData(this.fetchConfigs);
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAddWiFiProfile = await useStore.hasPermission("Can add WiFi Profile");
        this.canDeleteWiFiProfile = await useStore.hasPermission("Can delete WiFi Profile");
        this.canViewWiFiProfile = await useStore.hasPermission("Can view WiFi Profile");
      }
    },
    async fetchConfigs(schemaName = null) {
      try {
        const configs = await getAllWiFiProfiles(schemaName);
        this.configs = [...configs];
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching WiFi profiles:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    async fetchConfigsGlobal() {
      // Embedded under Organisations -> WiFi Profiles tab: iterate every
      // tenant schema and concat results for a cross-tenant view.
      const orgs = this.tenant.organizations.value || [];
      const all = [];
      for (const org of orgs) {
        if (!org.schema_name) continue;
        try {
          const rows = await getAllWiFiProfiles(org.schema_name);
          for (const r of (rows || [])) {
            all.push({ ...r, org_name: r.org_name || org.name, _orgSchema: org.schema_name });
          }
        } catch (e) {
          console.warn(`WiFi profile fetch failed for schema=${org.schema_name}:`, e);
        }
      }
      this.configs = all;
      this.applyFilters();
    },
    goToConfigDetail(config) {
      this.$router.push({
        name: "WiFiProfileDetails",
        params: { id: config.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    updateSelectedConfigs(selectedItems) {
      this.selectedConfigs = selectedItems.filter((item) => !item.marked_for_deletion);
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete") {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      await this.deleteSelectedConfigs();
      this.deletingItem = false;
      this.bulkAction = "";
    },
    async deleteSelectedConfigs() {
      if (this.selectedConfigs.length === 0) {
        alert("No profiles selected for deletion");
        return;
      }
      try {
        const deletePromises = this.selectedConfigs.map((config) =>
          deleteWiFiProfile(config.id, this.tenant.currentSchemaName?.value)
        );
        await Promise.all(deletePromises);
        this.fetchConfigs(this.tenant.currentSchemaName?.value);
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedConfigs = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting profiles. Please try again.");
      }
    },
    applyFilters() {
      const query = (this.searchQuery || "").toLowerCase();
      this.filteredConfigs = (this.configs || []).filter((config) => {
        const name = (config?.name || "").toLowerCase();
        const ssid = (config?.ssid || "").toLowerCase();
        return name.includes(query) || ssid.includes(query);
      });
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
      setPersistedPageSize(this.itemsPerPage);
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>
